import { WT_FALLBACK_MS } from "./constants";
import type { Transport } from "./transport";

export type TransportMode = "auto" | "webtransport" | "websocket";

export interface RaceOptions {
  mode: TransportMode;
  /** Starts a WebTransport attempt; null when WebTransport is unavailable (no browser support, or the server offers none). */
  startWebTransport: (() => Transport) | null;
  /** Message used when `mode` forces WebTransport but `startWebTransport` is null. */
  webTransportUnavailable?: string;
  startWebSocket: () => Transport;
  /** Start the WebSocket attempt after this long without a WebTransport handshake. */
  fallbackMs?: number;
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Waits for one transport; closes it when it fails. */
async function openOne(t: Transport): Promise<Transport> {
  try {
    await t.ready;
    return t;
  } catch (e) {
    t.close();
    throw e;
  }
}

/**
 * Opens the transport per docs/NETCODE.md "Transports": WebTransport first, the WebSocket attempt
 * starts after `fallbackMs` (3 s) or at once when WebTransport is unavailable or fails; the first
 * handshake to complete wins and the other attempt is closed. Forced modes try only their own
 * transport. Rejects when every attempt failed.
 */
export function raceTransports(opts: RaceOptions): Promise<Transport> {
  const { mode, startWebTransport, startWebSocket } = opts;
  if (mode === "websocket") return openOne(startWebSocket());
  if (mode === "webtransport") {
    if (!startWebTransport) {
      return Promise.reject(new Error(opts.webTransportUnavailable ?? "WebTransport is not available"));
    }
    return openOne(startWebTransport());
  }
  if (!startWebTransport) return openOne(startWebSocket());

  const { promise, resolve, reject } = Promise.withResolvers<Transport>();
  const attempts: Transport[] = [];
  const failures: string[] = [];
  let active = 0;
  let settled = false;
  let webSocketStarted = false;
  let timer = 0;

  const attempt = (t: Transport): void => {
    attempts.push(t);
    active++;
    t.ready.then(
      () => {
        if (settled) {
          t.close();
          return;
        }
        settled = true;
        clearTimeout(timer);
        for (const other of attempts) if (other !== t) other.close();
        resolve(t);
      },
      (e: unknown) => {
        active--;
        if (settled) return;
        failures.push(`${t.kind === "webtransport" ? "WebTransport" : "WebSocket"}: ${errorMessage(e)}`);
        if (t.kind === "webtransport") startWebSocketAttempt();
        if (active === 0 && webSocketStarted) {
          settled = true;
          clearTimeout(timer);
          reject(new Error(failures.join("; ")));
        }
      },
    );
  };

  const startWebSocketAttempt = (): void => {
    if (webSocketStarted || settled) return;
    webSocketStarted = true;
    clearTimeout(timer);
    attempt(startWebSocket());
  };

  try {
    attempt(startWebTransport());
  } catch (e) {
    failures.push(`WebTransport: ${errorMessage(e)}`);
    startWebSocketAttempt();
    return promise;
  }
  if (!webSocketStarted) timer = Number(setTimeout(startWebSocketAttempt, opts.fallbackMs ?? WT_FALLBACK_MS));
  return promise;
}
